import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation } from "react-router-dom";
import { AppHeader } from "@/components/layout/AppHeader";
import { ImportProgressBar } from "@/components/layout/ImportProgressBar";
import {
  getStoryImportClientConfig,
  subscribeToStoryImportJobs,
} from "@/features/story-import/api/storyImportClient";
import { useAuthStore } from "@/stores/authStore";
import { useStoryImportStore } from "@/stores/storyImportStore";

export function AppShell() {
  const { t } = useTranslation();
  const clientConfig = getStoryImportClientConfig();
  const location = useLocation();
  const isProjectFacingRoute = location.pathname === "/app" || location.pathname.startsWith("/projects/");
  const clearImportNotice = useStoryImportStore((state) => state.clearNotice);
  const continueProcessing = useStoryImportStore((state) => state.continueProcessing);
  const currentImportJobStatus = useStoryImportStore((state) => state.currentJob?.status);
  const currentImportJobId = useStoryImportStore((state) => state.currentJob?.jobId);
  const hydrateJob = useStoryImportStore((state) => state.hydrateJob);
  const hydrateLatestPendingJob = useStoryImportStore((state) => state.hydrateLatestPendingJob);
  const importNotice = useStoryImportStore((state) => state.notice);
  const userId = useAuthStore((state) => state.user?.id ?? null);

  useEffect(() => {
    if (currentImportJobId || !userId) {
      return;
    }

    void hydrateLatestPendingJob().catch(() => undefined);
  }, [currentImportJobId, hydrateLatestPendingJob, userId]);

  useEffect(() => {
    if (!userId) {
      return;
    }

    const dispose = subscribeToStoryImportJobs(userId, (jobId) => {
      void hydrateJob(jobId);
    });

    return () => {
      dispose();
    };
  }, [hydrateJob, userId]);

  useEffect(() => {
    if (
      !currentImportJobId
      || (currentImportJobStatus !== "queued"
        && currentImportJobStatus !== "pending"
        && currentImportJobStatus !== "processing"
        && currentImportJobStatus !== "canceling")
    ) {
      return;
    }

    void continueProcessing();
    const intervalId = window.setInterval(() => {
      void continueProcessing();
      void hydrateJob(currentImportJobId);
    }, clientConfig.progressPollMs);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [
    clientConfig.progressPollMs,
    continueProcessing,
    currentImportJobId,
    currentImportJobStatus,
    hydrateJob,
  ]);

  const showGlobalImportNotice = Boolean(importNotice)
    && !location.pathname.startsWith("/app/story-import");

  return (
    <div className="min-h-screen bg-canvas text-ink">
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-[22rem] bg-[radial-gradient(circle_at_top,rgba(199,150,75,0.18),transparent_48%)]" />
        <div className="absolute right-[-10rem] top-[18rem] h-[26rem] w-[26rem] rounded-full bg-[radial-gradient(circle,rgba(95,106,71,0.12),transparent_70%)]" />
        <div className="absolute left-[-12rem] top-[32rem] h-[24rem] w-[24rem] rounded-full bg-[radial-gradient(circle,rgba(159,82,54,0.12),transparent_72%)]" />
      </div>
      <AppHeader />
      <main
        className={[
          "mx-auto w-full px-4 py-6 sm:px-6 lg:px-8 xl:px-10",
          isProjectFacingRoute ? "max-w-[1560px]" : "max-w-7xl",
        ].join(" ")}
      >
        <ImportProgressBar />
        {showGlobalImportNotice ? (
          <div className="project-shell-notice mb-6" role={importNotice?.kind === "error" ? "alert" : "status"}>
            <div className="flex items-start justify-between gap-3">
              <p>{importNotice ? t(importNotice.message) : null}</p>
              <button
                className="text-muted-foreground transition-colors hover:text-foreground"
                onClick={clearImportNotice}
                type="button"
              >
                {t("projects.actions.dismiss")}
              </button>
            </div>
          </div>
        ) : null}
        <Outlet />
      </main>
    </div>
  );
}
